import { useCallback, useMemo, useState } from "react";
import { useEstimateQuote, useCreateQuote, useGenerateQuotePdf, type EstimateResult } from "../../features/quotes/mutations";
import { recomputeEstimate } from "../../features/quotes/calc";
import type {
  GeoPointInput,
  Quote,
  QuoteCalcInput,
  QuoteCargo,
  QuoteCustomerSnapshot,
  QuoteEstimate,
  QuoteOverrides,
  QuoteSchedule,
  TollItem,
  VehicleClass,
} from "../../features/quotes/types";
import { initialCalcInput, type QuoteSource } from "./quoteSource";

export type WizardStep = "service" | "calculation" | "result";
export const WIZARD_STEPS: WizardStep[] = ["service", "calculation", "result"];

export interface ServiceForm {
  route_id: string | null;
  origin: GeoPointInput | null;
  destination: GeoPointInput | null;
  crm_company_id: string | null;
  crm_contact_id: string | null;
  customer_snapshot: QuoteCustomerSnapshot;
  vehicle_class: VehicleClass | null;
  unit_id: string | null;
  cost_profile_id: string | null;
  schedule: QuoteSchedule;
  cargo: QuoteCargo;
}

export interface ResultForm {
  title: string;
  valid_until: string;
  notes: string;
  terms: string;
  show_breakdown: boolean;
}

function defaultValidUntil(): string {
  const d = new Date(Date.now() + 15 * 86400000);
  return d.toISOString().slice(0, 10);
}

/** Estado y acciones del cotizador en tres pasos. */
export function useQuoteWizard(source: QuoteSource) {
  const base = useMemo(() => initialCalcInput(source), [source]);
  const [step, setStep] = useState<WizardStep>("service");
  const [service, setService] = useState<ServiceForm>({
    route_id: base.route_id ?? null,
    origin: null,
    destination: null,
    crm_company_id: source.kind === "blank" ? source.crm_company_id ?? null : null,
    crm_contact_id: source.kind === "blank" ? source.crm_contact_id ?? null : null,
    customer_snapshot: {},
    vehicle_class: null,
    unit_id: null,
    cost_profile_id: null,
    schedule: base.schedule ?? { pickup_at: null, delivery_at: null, timezone: null },
    cargo: {},
  });
  const [overrides, setOverrides] = useState<QuoteOverrides>({});
  const [manualTolls, setManualTolls] = useState<TollItem[] | null>(null);
  const [distanceOverride, setDistanceOverride] = useState<number | null>(null);
  const [durationOverride, setDurationOverride] = useState<number | null>(null);
  const [server, setServer] = useState<EstimateResult | null>(null);
  const [result, setResult] = useState<ResultForm>({ title: "", valid_until: defaultValidUntil(), notes: "", terms: "", show_breakdown: false });
  const [saved, setSaved] = useState<Quote | null>(null);

  const estimateMut = useEstimateQuote();
  const createMut = useCreateQuote();
  const pdfMut = useGenerateQuotePdf();

  const calcInput = useCallback(
    (extra: Partial<QuoteCalcInput> = {}): QuoteCalcInput => ({
      ...base,
      route_id: service.route_id ?? base.route_id ?? null,
      origin: service.origin,
      destination: service.destination,
      vehicle_class: service.vehicle_class,
      unit_id: service.unit_id,
      cost_profile_id: service.cost_profile_id,
      schedule: service.schedule,
      cargo: service.cargo,
      distance_km: distanceOverride,
      duration_min: durationOverride,
      tolls_override: manualTolls ? { items: manualTolls } : null,
      overrides,
      ...extra,
    }),
    [base, service, distanceOverride, durationOverride, manualTolls, overrides],
  );

  const runEstimate = useCallback(
    async (extra: Partial<QuoteCalcInput> = {}) => {
      // El servidor calcula sin overrides: las líneas base deben seguir completas
      // para poder restaurar; `recomputeEstimate` aplica las ediciones encima.
      const res = await estimateMut.mutateAsync(calcInput({ overrides: {}, ...extra }));
      setServer(res);
      return res;
    },
    [estimateMut, calcInput],
  );

  /** Estimación mostrada: la del servidor con los overrides locales encima. */
  const estimate: QuoteEstimate | null = useMemo(() => {
    if (!server) return null;
    return recomputeEstimate({ estimate: server.estimate, overrides, manualTolls });
  }, [server, overrides, manualTolls]);

  const goToCalculation = useCallback(async () => {
    await runEstimate();
    setStep("calculation");
  }, [runEstimate]);

  const save = useCallback(async () => {
    const quote = await createMut.mutateAsync({
      ...calcInput(),
      crm_company_id: service.crm_company_id,
      crm_contact_id: service.crm_contact_id,
      customer_snapshot: service.customer_snapshot,
      title: result.title || null,
      valid_until: result.valid_until ? new Date(`${result.valid_until}T23:59:59`).toISOString() : null,
      notes: result.notes || null,
      terms: result.terms || null,
      show_breakdown: result.show_breakdown,
    });
    setSaved(quote);
    return quote;
  }, [createMut, calcInput, service, result]);

  const generatePdf = useCallback(async () => {
    if (!saved) return null;
    const meta = await pdfMut.mutateAsync({ id: saved.id, show_breakdown: result.show_breakdown });
    setSaved({ ...saved, pdf: meta });
    return meta;
  }, [pdfMut, saved, result.show_breakdown]);

  return {
    step,
    setStep,
    service,
    setService,
    overrides,
    setOverrides,
    manualTolls,
    setManualTolls,
    distanceOverride,
    setDistanceOverride,
    durationOverride,
    setDurationOverride,
    server,
    estimate,
    result,
    setResult,
    saved,
    runEstimate,
    goToCalculation,
    save,
    generatePdf,
    estimating: estimateMut.isPending,
    estimateError: estimateMut.error,
    saving: createMut.isPending,
    generatingPdf: pdfMut.isPending,
  };
}

export type QuoteWizard = ReturnType<typeof useQuoteWizard>;
